import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Copy, EnvelopeSimple, Trash, Users, IdentificationCard } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import ModalPortal from "@/components/ModalPortal";
import { usePersonalliggare } from "@/hooks/usePersonalliggare";
import PersonnummerModal from "@/components/PersonnummerModal";

function fmtHours(sec) {
  if (!sec) return "0h";
  const h = sec / 3600;
  return `${h.toFixed(1)}h`;
}

function absUrl(inviteUrl) {
  if (!inviteUrl) return "";
  if (/^https?:\/\//i.test(inviteUrl)) return inviteUrl;
  // Backend returned a path-only fallback — prepend the current origin.
  return `${window.location.origin}${inviteUrl.startsWith("/") ? "" : "/"}${inviteUrl}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Employees() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const { enabled: pllEnabled } = usePersonalliggare();
  const [pllUsers, setPllUsers] = useState({}); // user_id -> {has_personnummer, personnummer_masked, id06_number}
  const [pnrTarget, setPnrTarget] = useState(null);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee" });
  const [creating, setCreating] = useState(false);

  // Created invite link modal (shown after successful invite creation OR when admin clicks "Visa länk")
  const [linkModal, setLinkModal] = useState(null); // {kind:'invite'|'reset', email, name, role, invite_url, expires_at}
  const [sendingLink, setSendingLink] = useState(null); // user_id under skickning

  // Löneuppgifter (anst.nr + förvald arbetsplats) modal
  const [payrollTarget, setPayrollTarget] = useState(null);
  const [payrollForm, setPayrollForm] = useState({ employment_number: "", default_workplace_id: "" });
  const [payrollSaving, setPayrollSaving] = useState(false);
  const [workplacesList, setWorkplacesList] = useState([]);

  useEffect(() => {
    api.get("/workday/workplaces")
      .then((r) => setWorkplacesList((r.data || []).filter((w) => w.active !== false)))
      .catch(() => {});
  }, []);

  const openPayroll = (u) => {
    setPayrollTarget(u);
    setPayrollForm({
      employment_number: u.employment_number || "",
      default_workplace_id: u.default_workplace_id || "",
    });
  };

  const savePayroll = async () => {
    if (!payrollTarget) return;
    setPayrollSaving(true);
    try {
      // Skicka "" (tom sträng) för att rensa fält. `null` filtreras bort
      // i backend (v is not None) och skulle tyst no-op:a.
      await api.patch(`/users/${payrollTarget.user_id}`, {
        employment_number: payrollForm.employment_number || "",
        default_workplace_id: payrollForm.default_workplace_id || "",
      });
      toast.success("Löneuppgifter sparade");
      setPayrollTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte spara");
    } finally {
      setPayrollSaving(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const [u, s, inv] = await Promise.all([
        api.get("/users"),
        api.get("/users/stats"),
        api.get("/users/invites"),
      ]);
      setUsers(u.data);
      const map = {};
      s.data.forEach((row) => { map[row.user_id] = row; });
      setStats(map);
      setInvites(inv.data);
    } catch (e) {
      toast.error("Kunde inte hämta anställda");
    } finally {
      setLoading(false);
    }
  };

  const loadPllUsers = async () => {
    try {
      const r = await api.get("/personalliggare/users");
      const map = {};
      (r.data || []).forEach((u) => { map[u.user_id] = u; });
      setPllUsers(map);
    } catch {
      // silent — modulen kanske slås av just nu
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (pllEnabled) loadPllUsers();
    else setPllUsers({});
  }, [pllEnabled]);

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "employee" : "admin";
    if (!window.confirm(`Ändra rollen för ${u.name} till ${next === "admin" ? "Admin" : "Anställd"}?`)) return;
    try {
      await api.patch(`/users/${u.user_id}`, { role: next });
      toast.success("Roll uppdaterad");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.user_id}`, { active: !u.active });
      toast.success(u.active ? "Inaktiverad" : "Aktiverad");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(
      `Radera ${u.name} (${u.email})?\n\n` +
      "Användaren tas bort permanent. Tidigare tidrapporter behålls i databasen " +
      "men kommer att visas utan klickbar koppling till denna anställd.\n\n" +
      "Tips: Om du bara vill stoppa inloggningen, använd \"Inaktivera\" istället."
    );
    if (!ok) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`${u.name} raderad`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte radera");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/users/invite", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
      });
      toast.success("Inbjudan skapad — kopiera länken och skicka till den anställda");
      setShowInvite(false);
      setForm({ name: "", email: "", role: "employee" });
      setLinkModal({
        kind: "invite",
        email: r.data.email,
        name: r.data.name,
        role: r.data.role,
        invite_url: absUrl(r.data.invite_url),
        expires_at: r.data.expires_at,
      });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || "Kunde inte skapa inbjudan";
      const isDuplicate = err.response?.status === 409 || /finns redan/i.test(detail);
      if (isDuplicate) {
        // Hitta befintlig användare i listan och erbjud reset-link istället.
        const existing = users.find(
          (u) => (u.email || "").toLowerCase() === form.email.trim().toLowerCase()
        );
        if (existing) {
          toast.error(
            `${detail} — skickar återställningslänk via e-post istället…`,
            { duration: 3000 }
          );
          setShowInvite(false);
          setForm({ name: "", email: "", role: "employee" });
          // Skicka reset-länken direkt via mail (samma intention som "bjud in").
          await createResetLink(existing, true);
          return;
        }
      }
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Länk kopierad till urklipp");
    } catch {
      // fallback: select + execCommand
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Länk kopierad"); }
      catch { toast.error("Kunde inte kopiera — markera och kopiera manuellt"); }
      document.body.removeChild(ta);
    }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Avbryt inbjudan till ${inv.email}?`)) return;
    try {
      await api.delete(`/users/invites/${inv.invite_id}`);
      toast.success("Inbjudan avbruten");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte avbryta inbjudan");
    }
  };

  const createResetLink = async (u, sendEmail = false) => {
    setSendingLink(u.user_id);
    try {
      const r = await api.post(
        `/users/${u.user_id}/reset-link${sendEmail ? "?send_email=1" : ""}`
      );
      setLinkModal({
        kind: "reset",
        user_id: u.user_id,
        email: r.data.email,
        name: r.data.name,
        invite_url: r.data.reset_url,
        expires_at: r.data.expires_at,
        email_sent: !!r.data.email_sent,
        email_error: r.data.email_error || null,
      });
      if (sendEmail) {
        if (r.data.email_sent) {
          toast.success(`Länken skickades till ${r.data.email}`);
        } else if (r.data.email_error) {
          toast.error(r.data.email_error);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte skapa länk");
    } finally {
      setSendingLink(null);
    }
  };

  const sendResetEmailForModal = async () => {
    if (!linkModal?.user_id) return;
    setSendingLink(linkModal.user_id);
    try {
      const r = await api.post(
        `/users/${linkModal.user_id}/reset-link?send_email=1`
      );
      setLinkModal((prev) => prev && {
        ...prev,
        invite_url: r.data.reset_url,
        expires_at: r.data.expires_at,
        email_sent: !!r.data.email_sent,
        email_error: r.data.email_error || null,
      });
      if (r.data.email_sent) {
        toast.success(`Länken skickades till ${r.data.email}`);
      } else if (r.data.email_error) {
        toast.error(r.data.email_error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte skicka mail");
    } finally {
      setSendingLink(null);
    }
  };

  const sorted = useMemo(
    () => [...users].sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv")),
    [users]
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Anställda</h1>
          <p className="text-text-muted mt-1">
            {users.length} användare · {invites.length} väntande inbjudningar
          </p>
        </div>
        <button
          data-testid="invite-user-button"
          onClick={() => setShowInvite(true)}
          className="tk-btn-primary inline-flex items-center gap-2"
        >
          <EnvelopeSimple size={16} /> Bjud in anställd
        </button>
      </header>

      {/* Pending invites */}
      {invites.length > 0 && (
        <section className="tk-card p-4 md:p-6 space-y-3" data-testid="pending-invites-section">
          <h2 className="font-heading text-xl">Väntande inbjudningar</h2>
          <p className="text-xs text-text-muted">
            Kopiera länken och skicka den till den anställda via SMS, WhatsApp eller annat valfritt sätt. Länkar går ut efter 7 dagar.
          </p>
          <div className="divide-y divide-app">
            {invites.map((inv) => (
              <div
                key={inv.invite_id}
                data-testid={`invite-row-${inv.invite_id}`}
                className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="font-heading truncate">{inv.name}</div>
                  <div className="text-xs text-text-muted truncate">
                    {inv.email} · {inv.role === "admin" ? "Admin" : "Anställd"} · Går ut {fmtDate(inv.expires_at)}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`copy-invite-link-${inv.invite_id}`}
                    onClick={() => copyLink(absUrl(inv.invite_url))}
                    className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1"
                  >
                    <Copy size={14} /> Kopiera länk
                  </button>
                  <button
                    data-testid={`show-invite-link-${inv.invite_id}`}
                    onClick={() => setLinkModal({
                      email: inv.email, name: inv.name, role: inv.role,
                      invite_url: absUrl(inv.invite_url), expires_at: inv.expires_at,
                    })}
                    className="tk-btn-outline !py-2 !px-3 text-xs"
                  >
                    Visa länk
                  </button>
                  <button
                    data-testid={`cancel-invite-${inv.invite_id}`}
                    onClick={() => cancelInvite(inv)}
                    className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1 text-rust"
                    title="Avbryt inbjudan"
                  >
                    <Trash size={14} /> Avbryt
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="tk-card p-2 md:p-4">
        {loading ? (
          <p className="text-center text-text-muted py-12 text-sm">Laddar…</p>
        ) : sorted.length === 0 ? (
          <EmptyState
            testId="employees-empty"
            icon={Users}
            title="Inga anställda ännu"
            description="Bjud in dina medarbetare med e-post så får de en länk för att skapa sitt konto och börja stämpla tid."
            action={
              <button
                data-testid="empty-invite-button"
                onClick={() => setShowInvite(true)}
                className="tk-btn-primary inline-flex items-center gap-2"
              >
                <EnvelopeSimple size={16} /> Bjud in första anställd
              </button>
            }
          />
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-text-muted border-b border-app">
              <div className="col-span-4">Namn / Email</div>
              <div className="col-span-2">Roll</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Timmar</div>
              <div className="col-span-3 text-right">Åtgärder</div>
            </div>
            {sorted.map((u) => (
              <div
                key={u.user_id}
                data-testid={`employee-row-${u.user_id}`}
                className="grid grid-cols-12 px-4 py-4 border-b border-app last:border-0 items-center gap-y-2"
              >
                <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-surface-hover flex items-center justify-center text-text-muted">
                    {u.name?.[0] || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading truncate">{u.name}</div>
                    <div className="text-xs text-text-muted truncate">{u.email}</div>
                  </div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <span className={`tk-pill ${u.role === "admin" ? "tk-pill-warning" : ""}`}>
                    {u.role === "admin" ? "Admin" : "Anställd"}
                  </span>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <span className={`tk-pill ${u.active ? "tk-pill-active" : "tk-pill-danger"}`}>
                    {u.active ? "Aktiv" : "Inaktiv"}
                  </span>
                </div>
                <div className="col-span-6 md:col-span-1 tabular-nums">
                  {fmtHours(stats[u.user_id]?.total_seconds)}
                  <div className="text-[10px] text-text-muted uppercase tracking-wider">
                    {stats[u.user_id]?.report_count || 0} rap.
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 flex flex-wrap justify-end gap-2">
                  <button
                    data-testid={`send-reset-link-${u.user_id}`}
                    onClick={() => createResetLink(u, true)}
                    disabled={!u.active || sendingLink === u.user_id}
                    className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1"
                    title={u.active
                      ? `Skicka en engångslänk direkt till ${u.email} via e-post`
                      : "Kontot är inaktiverat — aktivera först"}
                  >
                    <EnvelopeSimple size={14} />
                    {sendingLink === u.user_id ? "Skickar…" : "Skicka länk"}
                  </button>
                  <button
                    data-testid={`edit-payroll-${u.user_id}`}
                    onClick={() => openPayroll(u)}
                    className={`tk-btn-outline !py-2 !px-3 text-xs ${
                      u.employment_number ? "text-[var(--brand-forest)]" : ""
                    }`}
                    title={
                      u.employment_number
                        ? `Anst.nr: ${u.employment_number}`
                        : "Sätt anst.nr & förvald arbetsplats (lönesystem)"
                    }
                  >
                    {u.employment_number ? `Anst.nr ${u.employment_number}` : "Löneuppgifter"}
                  </button>
                  {pllEnabled && (
                    <button
                      data-testid={`edit-personnummer-${u.user_id}`}
                      onClick={() => setPnrTarget({ ...u, ...(pllUsers[u.user_id] || {}) })}
                      className={`tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1 ${
                        pllUsers[u.user_id]?.has_personnummer ? "text-[var(--brand-forest)]" : ""
                      }`}
                      title={pllUsers[u.user_id]?.has_personnummer
                        ? `Personnr: ${pllUsers[u.user_id]?.personnummer_masked}`
                        : "Sätt personnummer & ID06"}
                    >
                      <IdentificationCard size={14} weight={pllUsers[u.user_id]?.has_personnummer ? "fill" : "regular"} />
                      {pllUsers[u.user_id]?.has_personnummer ? "Personnr ✓" : "Personnr"}
                    </button>
                  )}
                  <button
                    data-testid={`toggle-role-${u.user_id}`}
                    onClick={() => toggleRole(u)}
                    className="tk-btn-outline !py-2 !px-3 text-xs"
                  >
                    {u.role === "admin" ? "Gör anställd" : "Gör admin"}
                  </button>
                  <button
                    data-testid={`toggle-active-${u.user_id}`}
                    onClick={() => toggleActive(u)}
                    className="tk-btn-outline !py-2 !px-3 text-xs"
                  >
                    {u.active ? "Inaktivera" : "Aktivera"}
                  </button>
                  <button
                    data-testid={`delete-user-${u.user_id}`}
                    onClick={() => deleteUser(u)}
                    className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1 text-rust"
                    title="Radera anställd permanent"
                  >
                    <Trash size={14} /> Radera
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <ModalPortal>
        <div
          data-testid="invite-user-modal"
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !creating && setShowInvite(false)}
        >
          <div
            className="bg-surface border border-app rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl">Bjud in anställd</h2>
              <button
                onClick={() => !creating && setShowInvite(false)}
                className="text-text-muted hover:text-text-primary"
                aria-label="Stäng"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-text-muted mt-2">
              Skapar en engångslänk som den anställda öppnar för att välja sitt eget lösenord. Ingen e-post skickas — du delar länken själv (SMS / WhatsApp).
            </p>
            <form onSubmit={handleInvite} className="mt-4 space-y-3">
              <div>
                <label className="text-xs text-text-muted">Namn</label>
                <input
                  data-testid="invite-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="tk-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted">E-post</label>
                <input
                  data-testid="invite-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="tk-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-text-muted">Roll</label>
                <select
                  data-testid="invite-role-select"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="tk-input w-full"
                >
                  <option value="employee">Anställd</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  disabled={creating}
                  className="tk-btn-outline flex-1"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  data-testid="submit-invite-button"
                  disabled={creating}
                  className="tk-btn-primary flex-1"
                >
                  {creating ? "Skapar…" : "Skapa inbjudan"}
                </button>
              </div>
            </form>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Invite link modal */}
      {linkModal && (
        <ModalPortal>
        <div
          data-testid="invite-link-modal"
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setLinkModal(null)}
        >
          <div
            className="bg-surface border border-app rounded-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl">
                {linkModal.kind === "reset" ? "Ny inloggningslänk" : "Inbjudningslänk"}
              </h2>
              <button
                onClick={() => setLinkModal(null)}
                className="text-text-muted hover:text-text-primary"
                aria-label="Stäng"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-text-muted mt-2">
              {linkModal.kind === "reset" ? (
                linkModal.email_sent ? (
                  <>Inloggningslänken skickades via e-post till <strong className="text-text-primary">{linkModal.name}</strong> ({linkModal.email}). Klick på länken loggar in direkt — inget lösenord behövs. Länken går ut {fmtDate(linkModal.expires_at)}. Kopiera länken nedan om du vill dela via SMS/WhatsApp också.</>
                ) : (
                  <>Skicka inloggningslänken till <strong className="text-text-primary">{linkModal.name}</strong> ({linkModal.email}) via SMS eller WhatsApp. Klick på länken loggar in direkt — inget lösenord behövs. Länken går ut {fmtDate(linkModal.expires_at)}.</>
                )
              ) : (
                <>Skicka länken till <strong className="text-text-primary">{linkModal.name}</strong> ({linkModal.email}). Länken kan endast användas en gång och går ut {fmtDate(linkModal.expires_at)}.</>
              )}
            </p>
            <div className="mt-4">
              <label className="text-xs text-text-muted">Länk</label>
              <div className="flex gap-2 mt-1">
                <input
                  data-testid="invite-link-value"
                  readOnly
                  value={linkModal.invite_url}
                  className="tk-input w-full font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  data-testid="copy-invite-link-button"
                  onClick={() => copyLink(linkModal.invite_url)}
                  className="tk-btn-primary inline-flex items-center gap-1 shrink-0"
                >
                  <Copy size={14} /> Kopiera
                </button>
              </div>
              {linkModal.kind === "reset" && (
                <div className="mt-3">
                  {linkModal.email_sent ? (
                    <div
                      data-testid="reset-email-sent-badge"
                      className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 inline-flex items-center gap-2"
                    >
                      <EnvelopeSimple size={14} />
                      Länken skickades till {linkModal.email}
                    </div>
                  ) : (
                    <button
                      data-testid="send-reset-email-button"
                      onClick={sendResetEmailForModal}
                      disabled={sendingLink === linkModal.user_id}
                      className="tk-btn-outline inline-flex items-center gap-1 text-sm"
                    >
                      <EnvelopeSimple size={14} />
                      {sendingLink === linkModal.user_id
                        ? "Skickar…"
                        : `Skicka via e-post till ${linkModal.email}`}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setLinkModal(null)}
                className="tk-btn-outline"
                data-testid="close-invite-link-button"
              >
                Klart
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Reset password modal — REMOVED 2026-08-25. Magic Login (Skicka länk)
          har ersatt manuell lösenordssättning. Backend-endpoint kvarstår
          för super-admin edge-cases. */}
      {pnrTarget && (
        <PersonnummerModal
          user={pnrTarget}
          onClose={() => setPnrTarget(null)}
          onSaved={() => { loadPllUsers(); }}
        />
      )}

      {payrollTarget && (
        <ModalPortal>
          <div
            data-testid="payroll-details-modal"
            className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
            onClick={() => !payrollSaving && setPayrollTarget(null)}
          >
            <div
              className="bg-surface border border-app rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl">Löneuppgifter</h2>
                <button
                  onClick={() => !payrollSaving && setPayrollTarget(null)}
                  className="text-text-muted hover:text-text-primary"
                  aria-label="Stäng"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-text-muted mt-2">
                Uppgifter för <strong className="text-text-primary">{payrollTarget.name}</strong> som används i PAXml/CSV-export till Fortnox, Visma m.fl.
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-text-muted">
                    Anställningsnr <span className="text-text-muted/70">(matchar lönesystemets ID)</span>
                  </label>
                  <input
                    data-testid="employment-number-input"
                    type="text"
                    value={payrollForm.employment_number}
                    onChange={(e) => setPayrollForm({ ...payrollForm, employment_number: e.target.value })}
                    placeholder="t.ex. 001"
                    className="tk-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted">
                    Förvald arbetsplats <span className="text-text-muted/70">(används vid snabbstämpling)</span>
                  </label>
                  <select
                    data-testid="default-workplace-select"
                    value={payrollForm.default_workplace_id}
                    onChange={(e) => setPayrollForm({ ...payrollForm, default_workplace_id: e.target.value })}
                    className="tk-input w-full"
                  >
                    <option value="">— Ingen —</option>
                    {workplacesList.map((w) => (
                      <option key={w.workplace_id} value={w.workplace_id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setPayrollTarget(null)}
                    disabled={payrollSaving}
                    className="tk-btn-outline flex-1"
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    data-testid="save-payroll-details-btn"
                    onClick={savePayroll}
                    disabled={payrollSaving}
                    className="tk-btn-primary flex-1"
                  >
                    {payrollSaving ? "Sparar…" : "Spara"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}
